import type { DecisionLog, GraphTurnStamp, LmRunLog } from '@super/shared-types'
import { graphTurnStep, turnStampOf } from '@super/shared-types'

export type TimelineEntry =
  | { kind: 'run'; log: LmRunLog }
  | { kind: 'decision'; log: DecisionLog }

/** The rows one graph turn wrote, in pipeline order. */
export interface TurnGroup {
  stamp: GraphTurnStamp
  entries: TimelineEntry[]
  /** The sum of the turn's run durations. */
  modelMs: number
}

/** One thing the timeline shows: a turn section or a row outside any turn. */
export type TimelineBlock =
  | { kind: 'entry'; entry: TimelineEntry }
  | { kind: 'turn'; turn: TurnGroup }

function createdAtOf(entry: TimelineEntry): number {
  return new Date(entry.log.createdAt).getTime()
}

function stepOf(entry: TimelineEntry): number {
  return graphTurnStep(
    entry.kind === 'run' ? entry.log.promptType : entry.log.decisionType,
  )
}

export function turnStampOfEntry(entry: TimelineEntry): GraphTurnStamp | null {
  return entry.kind === 'run'
    ? turnStampOf(entry.log.inputContext)
    : turnStampOf(entry.log.inputs)
}

/**
 * Runs and decisions by time, with the rows of one turn folded into a section
 * at the place of its first row. Rows without a turn stamp stay on their own.
 */
export function timelineBlocks(
  runs: LmRunLog[],
  decisions: DecisionLog[],
): TimelineBlock[] {
  const entries: TimelineEntry[] = [
    ...runs.map<TimelineEntry>(log => ({ kind: 'run', log })),
    ...decisions.map<TimelineEntry>(log => ({ kind: 'decision', log })),
  ]
  entries.sort((a, b) => createdAtOf(a) - createdAtOf(b))

  const blocks: TimelineBlock[] = []
  const turns = new Map<string, TurnGroup>()
  for (const entry of entries) {
    const stamp = turnStampOfEntry(entry)
    if (!stamp) {
      blocks.push({ kind: 'entry', entry })
      continue
    }
    const modelMs = entry.kind === 'run' ? (entry.log.durationMs ?? 0) : 0
    const turn = turns.get(stamp.turnId)
    if (turn) {
      turn.entries.push(entry)
      turn.modelMs += modelMs
    } else {
      const made: TurnGroup = { stamp, entries: [entry], modelMs }
      turns.set(stamp.turnId, made)
      blocks.push({ kind: 'turn', turn: made })
    }
  }
  for (const turn of turns.values()) {
    turn.entries.sort(
      (a, b) => stepOf(a) - stepOf(b) || createdAtOf(a) - createdAtOf(b),
    )
  }
  return blocks
}
